import React, { useEffect, useState } from 'react';
import {
  Box,
  Button,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  IconButton,
  Stack,
  Switch,
  TextField,
  Typography
} from '@mui/material';
import { ArrowDownward, ArrowUpward, Save } from '@mui/icons-material';

function ColumnManagerDialog({
  open,
  section,
  config,
  onClose,
  onApply
}) {
  const [draftOrder, setDraftOrder] = useState([]);
  const [draftHidden, setDraftHidden] = useState([]);
  const [draftTitle, setDraftTitle] = useState('');
  const [draftShowTitle, setDraftShowTitle] = useState(true);

  useEffect(() => {
    if (!open || !section) return;
    setDraftOrder([...(config?.columnOrder || [])]);
    setDraftHidden([...(config?.hiddenKeys || [])]);
    setDraftTitle(config?.titleOverride || '');
    setDraftShowTitle(config?.showTitle !== false);
  }, [open, section, config]);

  if (!section) return null;

  const columnMap = Object.fromEntries((section.columns || []).map((col) => [col.key, col]));

  const moveColumn = (index, delta) => {
    const nextIndex = index + delta;
    if (nextIndex < 0 || nextIndex >= draftOrder.length) return;
    const next = [...draftOrder];
    const [item] = next.splice(index, 1);
    next.splice(nextIndex, 0, item);
    setDraftOrder(next);
  };

  const toggleHidden = (key) => {
    setDraftHidden((prev) => (
      prev.includes(key) ? prev.filter((k) => k !== key) : [...prev, key]
    ));
  };

  const visibleCount = draftOrder.filter((key) => !draftHidden.includes(key)).length;

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Manage columns — {section.title || section.name}</DialogTitle>
      <DialogContent dividers>
        <Typography variant="body2" color="text.secondary" paragraph>
          Reorder columns, hide columns you do not need, edit the section heading, and sort from the table headers.
        </Typography>
        <Stack spacing={2} sx={{ mb: 2 }}>
          <TextField
            label="Section heading"
            size="small"
            value={draftTitle}
            onChange={(e) => setDraftTitle(e.target.value)}
            helperText="Leave blank to use the original section heading"
          />
          <FormControlLabel
            control={(
              <Switch
                checked={draftShowTitle}
                onChange={(e) => setDraftShowTitle(e.target.checked)}
              />
            )}
            label="Show section heading"
          />
        </Stack>
        <Stack spacing={1}>
          {draftOrder.map((key, index) => {
            const col = columnMap[key];
            if (!col) return null;
            const hidden = draftHidden.includes(key);
            return (
              <Box
                key={key}
                display="flex"
                alignItems="center"
                gap={1}
                sx={{
                  px: 1,
                  py: 0.5,
                  border: '1px solid',
                  borderColor: 'divider',
                  borderRadius: 1,
                  bgcolor: hidden ? 'action.hover' : 'background.paper'
                }}
              >
                <Checkbox
                  size="small"
                  checked={!hidden}
                  onChange={() => toggleHidden(key)}
                />
                <Typography variant="body2" sx={{ flex: 1 }}>
                  {col.label}
                </Typography>
                <IconButton size="small" onClick={() => moveColumn(index, -1)} disabled={index === 0}>
                  <ArrowUpward fontSize="small" />
                </IconButton>
                <IconButton
                  size="small"
                  onClick={() => moveColumn(index, 1)}
                  disabled={index === draftOrder.length - 1}
                >
                  <ArrowDownward fontSize="small" />
                </IconButton>
              </Box>
            );
          })}
        </Stack>
        {visibleCount === 0 && (
          <Typography variant="caption" color="warning.main" sx={{ mt: 2, display: 'block' }}>
            Select at least one column to display.
          </Typography>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button
          variant="contained"
          startIcon={<Save />}
          onClick={() => onApply({
            columnOrder: draftOrder,
            hiddenKeys: draftHidden,
            titleOverride: draftTitle,
            showTitle: draftShowTitle
          })}
          disabled={visibleCount === 0}
        >
          Save
        </Button>
      </DialogActions>
    </Dialog>
  );
}

export default ColumnManagerDialog;
